import React, { useState, useEffect } from 'react';
import { api, Product, Supplier, Stock } from '../services/api';
import ConfirmModal from './ConfirmModal';
import LoadingSpinner from './LoadingSpinner';

const StockManagement: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [lowStockAlert, setLowStockAlert] = useState<Stock[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number>(0);
  const [editingStock, setEditingStock] = useState<Stock | null>(null);
  const [formData, setFormData] = useState({
    product_id: 0,
    quantity: 0,
    minimum_quantity: 0,
    supplier_id: 0
  });

  useEffect(() => {
    fetchStocks();
    fetchProducts();
    loadSuppliers();
  }, []);

  const fetchStocks = async () => {
    try {
      const response = await api.get('/stocks/');
      setStocks(response.data);
      setLowStockAlert(response.data.filter((s: Stock) => s.quantity <= s.minimum_quantity));
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/suppliers/');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation du fournisseur
    if (!selectedSupplierId || selectedSupplierId === 0) {
      alert('Veuillez sélectionner un fournisseur');
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        supplier_id: selectedSupplierId
      };
      
      if (editingStock) {
        await api.put(`/stocks/${editingStock.id}`, dataToSend);
      } else {
        await api.post('/stocks/', dataToSend);
      }
      fetchStocks();
      resetForm();
    } catch (error: any) {
      console.error('Error saving stock:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (stock: Stock) => {
    setEditingStock(stock);
    setFormData({
      product_id: stock.product_id,
      quantity: stock.quantity,
      minimum_quantity: stock.minimum_quantity,
      supplier_id: stock.supplier_id || 0
    });
    setSelectedSupplierId(stock.supplier_id || 0);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    setStockToDelete(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!stockToDelete) return;

    try {
      await api.delete(`/stocks/${stockToDelete}`);
      fetchStocks();
    } catch (error) {
      console.error('Error deleting stock:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ product_id: 0, quantity: 0, minimum_quantity: 0, supplier_id: 0 });
    setSelectedSupplierId(0);
    setEditingStock(null);
    setShowModal(false);
  };

  // Filtrer les produits qui n'ont pas encore de stock
  const availableProducts = products.filter(
    product => !stocks.some(stock => stock.product_id === product.id)
  );

  const handleSupplierChange = (supplierId: number) => {
    setSelectedSupplierId(supplierId);
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des stocks..." />;
  }

  return (
    <div className="stock-management">
      <div className="page-header">
        <h2>Gestion des Stocks</h2>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          ➕ Nouveau Stock
        </button>
      </div>

      {lowStockAlert.length > 0 && (
        <div className="alert-box">
          <h3>⚠️ Alertes Stock Faible</h3>
          {lowStockAlert.map(stock => (
            <div key={stock.id} className="alert-item">
              {stock.product_name}: {stock.quantity} restants (minimum: {stock.minimum_quantity})
            </div>
          ))}
        </div>
      )}

      {stocks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p className="empty-message">Aucun stock disponible</p>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            Ajouter votre premier stock
          </button>
        </div>
      ) : (
        <>
          {/* Desktop: Table View */}
          <div className="stocks-table-view">
            <div className="table-container">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité</th>
                    <th>Min</th>
                    <th>Dernière MAJ</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map(stock => (
                    <tr key={stock.id}>
                      <td><strong>{stock.product_name}</strong></td>
                      <td><span className="quantity-value">{stock.quantity}</span></td>
                      <td><span className="min-quantity">{stock.minimum_quantity}</span></td>
                      <td>{new Date(stock.last_updated).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <span className={`status-badge ${stock.quantity <= stock.minimum_quantity ? 'status-low' : 'status-ok'}`}>
                          {stock.quantity <= stock.minimum_quantity ? '⚠️ Faible' : '✅ OK'}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button className="btn-icon btn-edit" onClick={() => handleEdit(stock)} title="Modifier">
                            ✏️
                          </button>
                          <button className="btn-icon btn-delete" onClick={() => handleDelete(stock.id)} title="Supprimer">
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: Cards View */}
          <div className="stocks-cards-view">
            {stocks.map(stock => (
              <div key={stock.id} className={`stock-card ${stock.quantity <= stock.minimum_quantity ? 'low-stock' : ''}`}>
                <div className="stock-card-header">
                  <h3 className="stock-product-name">{stock.product_name}</h3>
                  <span className={`status-badge ${stock.quantity <= stock.minimum_quantity ? 'status-low' : 'status-ok'}`}>
                    {stock.quantity <= stock.minimum_quantity ? '⚠️ Faible' : '✅ OK'}
                  </span>
                </div>
                
                <div className="stock-card-body">
                  <div className="stock-info-grid">
                    <div className="stock-info-item">
                      <span className="info-label">Quantité actuelle</span>
                      <span className="info-value quantity-value">{stock.quantity}</span>
                    </div>
                    <div className="stock-info-item">
                      <span className="info-label">Quantité minimum</span>
                      <span className="info-value min-quantity">{stock.minimum_quantity}</span>
                    </div>
                    <div className="stock-info-item">
                      <span className="info-label">Dernière mise à jour</span>
                      <span className="info-value">{new Date(stock.last_updated).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="stock-card-actions">
                  <button className="btn-card btn-edit" onClick={() => handleEdit(stock)}>
                    <span className="btn-icon">✏️</span>
                    <span className="btn-text">Modifier</span>
                  </button>
                  <button className="btn-card btn-delete" onClick={() => handleDelete(stock.id)}>
                    <span className="btn-icon">🗑️</span>
                    <span className="btn-text">Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingStock ? 'Modifier le Stock' : 'Nouveau Stock'}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="sale-items">
              <div className="form-group">
                <label>Fournisseur *</label>
                <select
                  value={selectedSupplierId}
                  onChange={(e) => handleSupplierChange(Number.parseInt(e.target.value))}
                  className="product-select"
                  required
                >
                  <option value="0">Sélectionner un fournisseur</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>))
                  }
                </select>
              </div>

              <div className="form-group">
                <label>Produit *</label>
                {editingStock ? (
                  <input
                    type="text"
                    value={editingStock.product_name}
                    disabled
                    className="input-disabled"
                  />
                ) : (
                  <select
                    value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: Number.parseInt(e.target.value) })}
                    required
                  >
                    <option value="0">Sélectionner un produit</option>
                    {availableProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label>Quantité *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: Number.parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Quantité Minimum *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minimum_quantity}
                  onChange={(e) => setFormData({ ...formData, minimum_quantity: Number.parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingStock ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        title="Supprimer le stock"
        message="Êtes-vous sûr de vouloir supprimer ce stock ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirm(false);
          setStockToDelete(null);
        }}
      />
    </div>
  );
};

export default StockManagement;